import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  computeTreeBranchPaths,
} from '@/lib/treeBranchPathAuthority';

import {
  projectCanonicalTreeCompatibilityEdges,
} from './CanonicalTreeCompatibilityEdgeProjection';

import {
  projectCanonicalTreeLayoutPaths,
  type CanonicalTreeLayoutPathCompatibilityResult,
} from './CanonicalTreeLayoutPathCompatibilityProjection';

export type CanonicalTreeLayoutPathSelectorSource =
  | 'canonical-compatible'
  | 'legacy-fallback';

export interface CanonicalTreeLayoutPathRuntimeSelectorInput {
  readonly members:
    readonly GraphMember[];

  readonly viewerId:
    string | null | undefined;
}

export interface CanonicalTreeLayoutPathRuntimeSelectorResult {
  readonly branchPathByMemberId:
    ReadonlyMap<string, string>;

  readonly source:
    CanonicalTreeLayoutPathSelectorSource;

  readonly readiness:
    CanonicalTreeLayoutPathCompatibilityResult['readiness'];

  readonly legacyPathByMemberId:
    ReadonlyMap<string, string>;

  readonly canonicalCompatiblePathByMemberId:
    ReadonlyMap<string, string>;

  readonly parityCount: number;
  readonly canonicalAdditionCount: number;
  readonly legacyOnlyCount: number;
  readonly mismatchCount: number;

  readonly unresolvedEvidenceCount: number;
  readonly conflictingEvidenceCount: number;
  readonly invalidCompatibilityEvidenceCount: number;
}

const copyMap = (
  values:
    ReadonlyMap<string, string>,
): ReadonlyMap<string, string> =>
  new Map(values);

export const resolveCanonicalTreeLayoutPathRuntime = (
  input:
    CanonicalTreeLayoutPathRuntimeSelectorInput,
): CanonicalTreeLayoutPathRuntimeSelectorResult => {
  const members =
    [...input.members];

  const legacyPathByMemberId =
    computeTreeBranchPaths(
      members,
      input.viewerId,
    );

  if (
    !input.viewerId ||
    !members.some(
      member =>
        member.id === input.viewerId,
    )
  ) {
    return Object.freeze({
      branchPathByMemberId:
        copyMap(
          legacyPathByMemberId,
        ),

      source:
        'legacy-fallback',

      readiness:
        'BLOCKED',

      legacyPathByMemberId:
        copyMap(
          legacyPathByMemberId,
        ),

      canonicalCompatiblePathByMemberId:
        new Map(),

      parityCount:
        0,

      canonicalAdditionCount:
        0,

      legacyOnlyCount:
        legacyPathByMemberId.size,

      mismatchCount:
        0,

      unresolvedEvidenceCount:
        1,

      conflictingEvidenceCount:
        0,

      invalidCompatibilityEvidenceCount:
        0,
    });
  }

  const compatibility =
    projectCanonicalTreeCompatibilityEdges({
      members,
    });

  const projection =
    projectCanonicalTreeLayoutPaths({
      members,
      viewerId:
        input.viewerId,
      edges:
        compatibility.edges,
    });

  const compatibilityBlocked =
    compatibility.unresolvedEvidence.length > 0 ||
    compatibility.invalidEvidence.length > 0;

  const canonicalReady =
    !compatibilityBlocked &&
    (
      projection.readiness === 'READY' ||
      projection.readiness ===
        'READY-WITH-CANONICAL-ADDITIONS'
    );

  const selected =
    canonicalReady
      ? projection.layoutPathByMemberId
      : legacyPathByMemberId;

  return Object.freeze({
    branchPathByMemberId:
      copyMap(selected),

    source:
      canonicalReady
        ? 'canonical-compatible'
        : 'legacy-fallback',

    readiness:
      canonicalReady
        ? projection.readiness
        : 'BLOCKED',

    legacyPathByMemberId:
      copyMap(
        legacyPathByMemberId,
      ),

    canonicalCompatiblePathByMemberId:
      copyMap(
        projection.layoutPathByMemberId,
      ),

    parityCount:
      projection.parityCount,

    canonicalAdditionCount:
      projection.canonicalAdditionCount,

    legacyOnlyCount:
      projection.legacyOnlyCount,

    mismatchCount:
      projection.mismatchCount,

    unresolvedEvidenceCount:
      compatibility.unresolvedEvidence.length +
      projection.unresolvedEvidenceCount,

    conflictingEvidenceCount:
      projection.conflictingEvidenceCount,

    invalidCompatibilityEvidenceCount:
      compatibility.invalidEvidence.length,
  });
};
